export const SHELL_AGENT_STORAGE_KEY = "ano-shell-agent";
export const LAST_STARTED_SHELL_AGENT_STORAGE_KEY =
  "ano-last-started-shell-agent";
export const SHELL_AGENT_ENV_KEY = "ANO_SHELL_AGENT";

export const SHELL_AGENT_IDS = ["claude", "codex", "gemini"] as const;
export type ShellAgentId = (typeof SHELL_AGENT_IDS)[number];

export const DEFAULT_SHELL_AGENT_ID: ShellAgentId = "claude";

export const SHELL_AGENT_OPTIONS: ReadonlyArray<{
  value: ShellAgentId;
  label: string;
  command: string;
}> = [
  { value: "claude", label: "Claude", command: "claude" },
  { value: "codex", label: "Codex", command: "codex" },
  { value: "gemini", label: "Gemini", command: "gemini" },
];

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function isShellAgentId(value: string): value is ShellAgentId {
  return (SHELL_AGENT_IDS as readonly string[]).includes(value);
}

export function readShellAgentPreference(): ShellAgentId {
  const storage = getStorage();
  if (!storage) return DEFAULT_SHELL_AGENT_ID;

  try {
    const storedAgent = storage.getItem(SHELL_AGENT_STORAGE_KEY);
    if (storedAgent && isShellAgentId(storedAgent)) {
      return storedAgent;
    }
  } catch {
    // Ignore storage read failures and use the default shell agent.
  }

  return DEFAULT_SHELL_AGENT_ID;
}

export function setShellAgentPreference(agentId: ShellAgentId): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(SHELL_AGENT_STORAGE_KEY, agentId);
  } catch {
    // Ignore storage write failures; the in-memory selection still updates.
  }
}

export function readLastStartedShellAgent(): ShellAgentId | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const storedAgent = storage.getItem(LAST_STARTED_SHELL_AGENT_STORAGE_KEY);
    if (storedAgent && isShellAgentId(storedAgent)) {
      return storedAgent;
    }
  } catch {
    // Ignore storage read failures; the caller can treat this as unknown.
  }

  return null;
}

export function setLastStartedShellAgent(agentId: ShellAgentId): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(LAST_STARTED_SHELL_AGENT_STORAGE_KEY, agentId);
  } catch {
    // Ignore storage write failures; reconnect will conservatively respawn.
  }
}

export function shouldReuseShellAgentPty({
  forceNewPty,
  selectedAgent,
  lastStartedAgent,
}: {
  forceNewPty: boolean | undefined;
  selectedAgent: ShellAgentId;
  lastStartedAgent: ShellAgentId | null;
}): boolean {
  if (forceNewPty) return false;
  if (lastStartedAgent === selectedAgent) return true;
  return lastStartedAgent == null && selectedAgent === DEFAULT_SHELL_AGENT_ID;
}
